import * as Sentry from "@sentry/react";
import React, { useCallback, useState, useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import ShadowDOMWrapper from "../components/ShadowDOMWrapper";
import Api from "../lib/api";
import { trackApplicationStarted } from "../lib/tracking";
import { captureUtmParameters } from "../lib/utmTracking";
import { pushUtmData } from "../lib/dataLayer";
import { getReferredByValue } from "../lib/referralMapping";

interface Props {
  code?: string;
}

const api = new Api();

export const StartAuthCodeStatic: React.FC<RouteComponentProps & Props> = ({
  history,
  code,
}) => {
  const [cssContent, setCssContent] = useState<string>("");
  const [fontsLoaded, setFontsLoaded] = useState<boolean>(false);
  const [codeValidating, setCodeValidating] = useState<boolean>(false);

  useEffect(() => {
    // Capture UTM parameters on page load and push to dataLayer
    captureUtmParameters();
    pushUtmData();
  }, []);

  useEffect(() => {
    // Load fonts using FontFace API
    const loadFonts = async () => {
      try {
        const fonts = [
          new FontFace(
            "ABC Monument Grotesk",
            "url(/static-assets/fonts/ABCMonumentGrotesk-Regular.woff2)",
            { weight: "400", style: "normal" },
          ),
          new FontFace(
            "ABC Monument Grotesk",
            "url(/static-assets/fonts/ABCMonumentGrotesk-RegularItalic.woff2)",
            { weight: "400", style: "italic" },
          ),
          new FontFace(
            "ABC Monument Grotesk",
            "url(/static-assets/fonts/ABCMonumentGrotesk-Bold.woff2)",
            { weight: "700", style: "normal" },
          ),
          new FontFace(
            "ABC Monument Grotesk",
            "url(/static-assets/fonts/ABCMonumentGrotesk-BoldItalic.woff2)",
            { weight: "700", style: "italic" },
          ),
        ];

        // Add fonts to document
        fonts.forEach((font) => document.fonts.add(font));

        // Load all fonts
        await Promise.all(fonts.map((font) => font.load()));

        setFontsLoaded(true);
      } catch (error) {
        console.error("Failed to load fonts:", error);
        setFontsLoaded(true); // Continue anyway with fallback fonts
      }
    };

    loadFonts();
  }, []);

  useEffect(() => {
    // Load the CSS file
    fetch("/static-assets/frontend-styles.css")
      .then((response) => response.text())
      .then((css) => {
        // Replace :root selectors with :host to work in shadow DOM
        const shadowCSS = css.replace(/:root/g, ":host");
        setCssContent(shadowCSS);
      })
      .catch((error) => console.error("Failed to load CSS:", error));
  }, []);

  // Validate code from URL if provided
  useEffect(() => {
    if (code) {
      setCodeValidating(true);
      api
        .validateSecurityCode(code)
        .then((result) => {
          if (!result.valid) {
            console.warn("Invalid security code provided:", code);
            // Redirect to /mentor without code
            history.replace("/mentor");
          }
        })
        .catch((error) => {
          console.error("Error validating security code:", error);
          // On error, redirect to /mentor without code
          history.replace("/mentor");
        })
        .finally(() => {
          setCodeValidating(false);
        });
    }
  }, [code, history]);

  const handleShadowMount = useCallback(
    (shadowRoot: ShadowRoot) => {
      const form = shadowRoot.querySelector("form");
      if (!form) return;

      // Get form elements
      const emailInput = shadowRoot.querySelector<HTMLInputElement>("#email");
      const codeInput = shadowRoot.querySelector<HTMLInputElement>("#code");
      const notAffiliatedCheckbox =
        shadowRoot.querySelector<HTMLInputElement>("#not-affiliated");
      const acceptCheckbox =
        shadowRoot.querySelector<HTMLInputElement>("#accept");
      const errorDiv =
        shadowRoot.querySelector<HTMLDivElement>("#error-message");
      const codeErrorDiv =
        shadowRoot.querySelector<HTMLDivElement>("#code-error");
      const codeField = shadowRoot.querySelector<HTMLDivElement>("#code-field");
      const submitButton = shadowRoot.querySelector<HTMLButtonElement>(
        'button[type="submit"]',
      );

      // Handle Citizens Mentor checkbox toggle
      if (notAffiliatedCheckbox && codeField && codeInput) {
        notAffiliatedCheckbox.addEventListener("change", (e) => {
          const checked = (e.target as HTMLInputElement).checked;

          if (checked) {
            // Hide code field and set code to ACPUSA
            codeField.style.display = "none";
            codeInput.value = "ACPUSA";
            codeInput.removeAttribute("required");
            // Clear any code errors
            if (codeErrorDiv) {
              codeErrorDiv.style.display = "none";
              codeErrorDiv.textContent = "";
            }
          } else {
            // Show code field and clear the ACPUSA code
            codeField.style.display = "flex";
            codeInput.value = "";
            codeInput.setAttribute("required", "true");
          }
        });
      }

      form.addEventListener("submit", async (e) => {
        e.preventDefault();

        if (!emailInput || !acceptCheckbox) return;

        // Clear previous errors
        if (errorDiv) {
          errorDiv.style.display = "none";
          errorDiv.textContent = "";
        }

        // Validation
        const email = emailInput.value.trim();
        const emailRegex = /^[^ @]+@[^ @]+\.[^ @][^ @]+$/;
        const currentCode = codeInput ? codeInput.value.trim() : "";
        const isNotAffiliated = notAffiliatedCheckbox
          ? notAffiliatedCheckbox.checked
          : false;

        // Clear previous errors
        if (codeErrorDiv) {
          codeErrorDiv.style.display = "none";
          codeErrorDiv.textContent = "";
        }

        if (!email) {
          if (errorDiv) {
            errorDiv.textContent = "Please enter your email address";
            errorDiv.style.display = "block";
          }
          return;
        }

        if (!emailRegex.test(email)) {
          if (errorDiv) {
            errorDiv.textContent = "Please enter a valid email address";
            errorDiv.style.display = "block";
          }
          return;
        }

        // Code validation: required unless Citizens Mentor is checked
        if (!code && !isNotAffiliated && !currentCode) {
          if (codeErrorDiv) {
            codeErrorDiv.textContent =
              "You must supply the code you were given.";
            codeErrorDiv.style.display = "block";
          }
          return;
        }

        if (!acceptCheckbox.checked) {
          if (errorDiv) {
            errorDiv.textContent = "You must accept the terms to continue";
            errorDiv.style.display = "block";
          }
          return;
        }

        // Disable submit button
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = "Processing...";
        }

        try {
          const utmData = captureUtmParameters();
          const referredBy = getReferredByValue();

          // Determine security code
          const securityCode =
            code || currentCode || (isNotAffiliated ? "ACPUSA" : "");

          // Call API using the same method as original StartAuthCode
          const newApp = {
            email,
            agree: "agree",
            securityCode,
            referredBy,
            ...(utmData && { utmData }),
          };

          const response = await api.createApplicationSecurityCode(newApp);

          if (response.data.success) {
            // Track application started event
            // Security codes are always for mentor applications (mentor-corp or mentor-ctzn)
            trackApplicationStarted({
              applicationType: "mentor",
              applicationId: response.data.message,
            });

            // Navigate to survey
            history.push(`/a/${response.data.message}`);
          } else {
            if (errorDiv) {
              errorDiv.textContent =
                response.data.message ||
                "Something went wrong. Please try again.";
              errorDiv.style.display = "block";
            }
          }
        } catch (error) {
          if (errorDiv) {
            errorDiv.textContent =
              "Connection error. Please check your internet and try again.";
            errorDiv.style.display = "block";
          }
          Sentry.captureException(error);
        } finally {
          // Re-enable submit button
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = "Submit";
          }
        }
      });
    },
    [history, code],
  );

  const htmlContent = `
    <body class="wp-singular page-template page-template-template-apply-mentor page-template-template-apply-mentor-php page page-id-6054 wp-embed-responsive wp-theme-american-corporate-partners test-apply-become-a-mentor">
    <div class="lg:flex">
      <div class="relative flex w-full items-end min-h-35 lg:min-h-none lg:h-screen lg:sticky lg:top-0">
        <div class="lg:relative flex h-full w-full items-end p-3 lg:p-5">
          <div class="absolute top-3 left-3 z-5 inline-flex grow-0 basis-1/2 shrink-1 max-w-9 lg:max-w-12 lg:top-5 lg:left-5">
            <a data-header-logo class="inline-flex w-full object-contain" href="https://www.acp-usa.org/">
              <img src="/logo-white.svg" alt="American Corporate Partners" />
              <span class="sr-only">Go to home page</span>
            </a>
          </div>
         <h1 class="relative text-white max-w-[15ch] z-5">
          Become a Mentor. Make a difference.
          </h1>
          <div class="absolute inset-0">
            <picture>
              <source media="(max-width: 640px)" srcset="/static-assets/mentor-hero-sm.jpg" />
              <source media="(max-width: 1280px)" srcset="/static-assets/mentor-hero-md.jpg" />
              <img src="/static-assets/mentor-hero.jpg" alt="Mentor Image" class="h-full w-full object-cover" />
            </picture>
            <div class="pointer-events-none absolute bottom-0 left-0 h-full w-full z-1" style="background: linear-gradient(to left, transparent, rgba(0,0,0,0.5))"></div>
          </div>
        </div>
      </div>
      <div class="bg-white p-3 lg:p-5 lg:max-w-50 text-deemphasized">
        <div class="w-full">
          <h2 class="text-lg text-brave-blue">Get started</h2>
          <p>
            Thank you for your interest in volunteering to be an ACP Mentor. To get started, fill out this application. The
            application takes about 15 minutes to complete.
          </p>

          <ul class="list-disc ml-[1.5em] mb-3">
            <li>The ideal ACP Mentor is 35+ years of age and has 6+ years of professional experience.</li>
            <li>Mentors will be paired with a Protégé for a yearlong mentorship to provide professional guidance in the Protégé's
                areas of interest. Pairs are expected to communicate once a month.</li>
            <li>Discussions will focus on topics that are important to the Protégé, such as résumé writing, transitioning to a
                corporate environment, networking, or small business planning.</li>
            <li>Mentor applicants can expect to be contacted by ACP via email within 1-3 business days of submitting an application.</li>
          </ul>

          <div class="rounded-md p-3 text-deemphasized bg-light-grey">
            <p class="font-bold text-brave-blue">Enter your email address to begin</p>
            <div id="error-message" style="display: none; background: #fed7d7; color: #c53030; padding: 12px; border-radius: 4px; margin-bottom: 16px;"></div>
            <form>
              <div class="flex flex-col gap-3">
                <label for="email" class="sr-only">Email address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Email address"
                  required
                  class="rounded-md bg-white p-1 text-sm leading-none transition-colors duration-300 focus-visible:outline-3 focus-visible:outline-brave-blue/40 text-brave-blue focus-visible:outline-offset-2"
                />
                ${
                  !code
                    ? `
                <div id="code-field" class="flex flex-col gap-1">
                  <label for="code" class="text-sm font-medium text-brave-blue">If you were provided with an access code,</label>
                  <input
                    type="password"
                    id="code"
                    name="code"
                    placeholder="Enter your access code"
                    required
                    class="rounded-md bg-white p-1 text-sm leading-none transition-colors duration-300 focus-visible:outline-3 focus-visible:outline-brave-blue/40 text-brave-blue focus-visible:outline-offset-2"
                  />
                  <div id="code-error" style="display: none; color: #c53030; font-size: 0.875rem; margin-top: 4px;"></div>
                </div>
                `
                    : ""
                }
                ${
                  !code
                    ? `

                
                <div class="flex items-start gap-1">
                  <input type="checkbox" id="not-affiliated" name="not-affiliated" />
                  <label for="not-affiliated" class="relative -top-[0.35em] text-balance font-medium">
                    <span>I am not affiliated with an ACP Corporate Partner, but would like to apply as an ACP Citizens Mentor.</span>
                  </label>
                </div>
                `
                    : ""
                }
                <div class="flex items-start gap-1">
                  <input type="checkbox" id="accept" name="accept" required />
                  <label for="accept" class="relative -top-[0.35em] text-balance">
                    I accept that ACP staff will use some or all of the information provided in this application as a means
                    of introduction to my Protégé.
                  </label>
                </div>
                <div class="block">
                  <button type="submit" class="button button-bright-blue">Submit</button>
                </div>
              </div>
            </form>
          </div>

          <div class="mb-3 rounded-md p-3 mt-1.5 bg-light-grey">
            <h2 class="text-base text-brave-blue">Questions?</h2>
            <p class="text-deemphasized max-w-[28ch]">
              Send us an email at <a href="mailto:info@acp-usa.org">info@acp-usa.org</a> or give us a call at (212) 752-0700.
            </p>
          </div>
        </div>
      </div>
    </div>
    </body>
  `;

  // Wait for both CSS and fonts to load before rendering
  // Also wait for code validation if a code was provided
  if (!cssContent || !fontsLoaded || (code && codeValidating)) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "white",
        }}
      >
        <style>
          {`
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
                opacity: 1;
              }
              50% {
                transform: scale(1.05);
                opacity: 0.9;
              }
            }
            .loading-logo {
              animation: pulse 1.5s ease-in-out infinite;
            }
          `}
        </style>
        <img
          src="/logo-2019.color.whitebg.small.png"
          alt="American Corporate Partners"
          className="loading-logo"
          style={{
            width: "200px",
            height: "auto",
          }}
        />
        <p style={{ marginTop: "20px", color: "#666", fontSize: "14px" }}>
          Loading...
        </p>
      </div>
    );
  }

  return (
    <ShadowDOMWrapper
      html={htmlContent}
      styles={cssContent}
      onMount={handleShadowMount}
    />
  );
};

export default StartAuthCodeStatic;
